import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import { useTranslation } from 'react-i18next';

interface ImageFolder {
  id: string;
  name: string;
  createdAt: string;
  createdBy: string;
  imageCount?: number;
}

interface FolderDeleteModalProps {
  open: boolean;
  onClose: () => void;
  folder: ImageFolder | null;
  onDelete: (folderId: string) => Promise<void>;
}

const FolderDeleteModal: React.FC<FolderDeleteModalProps> = ({
  open,
  onClose,
  folder,
  onDelete
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal kapanırken temizle
  const handleClose = () => {
    if (isDeleting) return;
    setError(null);
    onClose();
  };

  // Klasör sil
  const handleDelete = async () => {
    if (!folder) return;

    setIsDeleting(true);
    setError(null);

    try {
      await onDelete(folder.id);
      
      // State güncellemesinin tamamlanması için kısa bir gecikme
      await new Promise(resolve => setTimeout(resolve, 100));
      
      handleClose();
    } catch (error: any) {
      console.error('Klasör silme hatası:', error);
      // Backend'den gelen hata mesajını göster
      const errorMessage = error?.message || tt('Klasör silinirken bir hata oluştu', 'An error occurred while deleting folder');
      setError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!folder) return null;

  const hasImages = (folder.imageCount || 0) > 0;

  return (
    <Dialog
      open={open}
      onClose={!isDeleting ? handleClose : undefined}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle 
        sx={{ 
          display: 'flex', 
          alignItems: 'center',
          color: 'error.main'
        }}
      >
        <WarningIcon sx={{ mr: 1 }} />
        {tt('Klasör Silme Onayı', 'Folder Delete Confirmation')}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            {tt('Aşağıdaki klasörü silmek istediğinizden emin misiniz?', 'Are you sure you want to delete the following folder?')}
          </Typography>
          
          <Box 
            sx={{ 
              p: 2, 
              bgcolor: 'grey.100', 
              borderRadius: 1, 
              border: '1px solid',
              borderColor: 'grey.300',
              my: 2
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              📁 {folder.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {folder.imageCount || 0} {tt('resim', 'images')} • {new Date(folder.createdAt).toLocaleDateString(localeCode)}
            </Typography>
          </Box>
        </Box>

        {hasImages && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{tt('Dikkat:', 'Warning:')}</strong> {tt('Bu klasörde', 'This folder contains')} <strong>{folder.imageCount} {tt('resim', 'images')}</strong>. {tt('Klasör silindiğinde bu resimlerin bağlantıları koparabilir.', 'When the folder is deleted, links to these images may break.')}
            </Typography>
          </Alert>
        )}

        <Alert severity="info">
          <Typography variant="body2">
            <strong>{tt('Not:', 'Note:')}</strong> {tt('Klasör pasif duruma getirilecektir. Veriler kalıcı olarak silinmeyecektir.', 'The folder will be marked passive. Data will not be permanently deleted.')}
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={handleClose} 
          color="inherit"
          disabled={isDeleting}
        >
          {tt('İptal', 'Cancel')}
        </Button>
        <Button 
          onClick={handleDelete} 
          variant="contained"
          color="error"
          disabled={isDeleting}
          startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
        >
          {isDeleting ? tt('Siliniyor...', 'Deleting...') : tt('Evet, Sil', 'Yes, Delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FolderDeleteModal; 